import { useEffect } from 'react';
import { useAuth0 } from '@brighte/passwordless-authenticator';
import { PackageQuote } from '@graphql';
import { usePackageContext } from '@provider';
import { AnalyticsEvent } from './constants';
import {
  identifyAnalyticsUser,
  pageAnalyticsEvent,
  trackAnalyticsEvent,
  transformBaseAnalyticsPayload,
  transformEventAnalyticsPayload,
} from './helpers';

type TrackSubmissionEventsProps = {
  eventName: AnalyticsEvent;
  extra?: Record<string, string | number | boolean>;
  quotes?: Array<PackageQuote>;
};

export function useAnalytics(page?: string) {
  const { packageForm } = usePackageContext();
  const { user } = useAuth0();

  useEffect(() => {
    // send page view events on page mount
    if (page && window) {
      pageAnalyticsEvent(page, {
        title: page,
        url: window.location.href,
        path: window.location.pathname,
        referrer: document.referrer,
        search: window.location.search,
      });
    }
  }, [page]);

  const trackAndIdentifySignIn = async () => {
    const { email, given_name, family_name, role, uid, userId } = user || {};

    const traits = {
      brighte_id: userId,
      email: email,
      first_name: given_name,
      last_name: family_name,
      role_type: role,
    };

    if (uid) {
      identifyAnalyticsUser(uid, traits);
      trackAnalyticsEvent(AnalyticsEvent.SignedIn, traits);
    }
  };

  const trackSubmissionEvents = ({ eventName, extra, quotes }: TrackSubmissionEventsProps) => {
    if (!user) return;

    const baseAnalyticsData = transformBaseAnalyticsPayload({
      opportunityId: packageForm.opportunityId,
      financialProductId: packageForm.financialProduct?.id,
      quotes,
      user,
    });

    const packageEventPayloadData = transformEventAnalyticsPayload({
      eventName,
      quotes,
    });

    trackAnalyticsEvent(eventName, {
      ...baseAnalyticsData,
      ...extra,
      ...(packageEventPayloadData && { package_details: packageEventPayloadData }),
    });
  };

  return { trackAndIdentifySignIn, trackSubmissionEvents };
}
